/*  This file uses certain naming conventions for function:

Validation rule getters (which are called with an argument to get the validation rule) are prefixed with:
 `ruleGetter`

Lists of multiple rules are prefixed with:
 `rules`

Everything else is assumed to be a validation rule, unless its name clearly indicates it is something else.
 */

import { defineRule } from 'vee-validate';
import { isValidEmailAddress } from '@/util/emailUtils';

export type TypeRuleInput = string | number;
// A list of functions that expect strings or numbers
export type TypeValidationRule = (value: TypeRuleInput) => true | string;
export type TypeValidationRuleString = (value: string) => true | string;
export type TypeValidationRuleNumber = (value: number) => true | string;
export type TypeValidationRules = TypeValidationRule[];

function decoratorAllowEmptyInput(underlyingRule: TypeValidationRule): TypeValidationRule {
    return (value: TypeRuleInput): true | string => {
        if (value === '') {
            return true;
        }
        return underlyingRule(value);
    };
}

export function required(value: TypeRuleInput): true | string {
    if (typeof value === 'number') {
        return true; // So that 0 works for numbers
    }
    if (typeof value === 'string') {
        return !!value || 'This field is required';
    }
    if (value === null) {
        return 'This field is required';
    }
    if (typeof value === 'object') {
        return true;
    }
    return `required: Unexpected type [[${typeof value}]]`;
}

// Do ruleGetterMinLength(3) to get the validation rule
export function ruleGetterMinLength(minLength: number): TypeValidationRuleString {
    return decoratorAllowEmptyInput((value: string): true | string => {
        return (
            (value || '').length >= minLength ||
            `This field must be at least ${minLength} characters long`
        );
    });
}
export const minLength3 = ruleGetterMinLength(3);
export const rulesRequiredMinLength3 = [required, minLength3];
export const minLength2 = ruleGetterMinLength(2);

// Do ruleGetterMaxLength(250) to get the validation rule
export function ruleGetterMaxLength(maxLength: number): TypeValidationRuleString {
    return decoratorAllowEmptyInput((value: string): true | string => {
        return (
            (value || '').length <= maxLength ||
            `This field must be at most ${maxLength} characters long`
        );
    });
}
export const maxLength250 = ruleGetterMaxLength(250);
export const rulesRequiredMaxLength250 = [required, maxLength250];

export const email = decoratorAllowEmptyInput((value: string): true | string => {
    return isValidEmailAddress(value) || 'Email must be valid';
});
export const rulesRequiredEmail = [required, email];

export const rulesPassword: TypeValidationRuleString[] = [required];

export function helperFunctionisYYYYMMDDDateStringWithDashes(dateString: string): boolean {
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;

    if (!datePattern.test(dateString)) {
        return false;
    }

    const [year, month, day] = dateString.split('-').map(Number);

    if (year < 1900 || year > 2100) {
        return false;
    }

    if (month < 1 || month > 12) {
        return false;
    }

    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
        return false;
    }

    return true;
}

export const isYYYYMMDDDateStringWithDashes = decoratorAllowEmptyInput(
    (value: string): true | string => {
        return (
            helperFunctionisYYYYMMDDDateStringWithDashes(value) ||
            'Invalid date format, please enter YYYY-MM-DD'
        );
    },
);

function getErrorMessageWhenShouldBeANumberButIsnt(value: string): string {
    if (typeof value === 'number') {
        throw new Error(
            'Value is a number, but expected this function to be called with a non-numeric value',
        );
    }
    if (!(typeof value === 'string')) {
        throw new Error(
            'Value is not a string, but expected this function to be called with a string value',
        );
    }
    if (!isNaN(parseFloat(value))) {
        return 'This field must be an number, but seems to be a string of an number.  Did the PROGRAMMER forget to use v-model.number?';
    }
    return 'This field must be a number';
}

function decoratorExpectNumeric(underlyingRule: TypeValidationRule): TypeValidationRule {
    return (value: TypeRuleInput): true | string => {
        if (typeof value !== 'number') {
            return getErrorMessageWhenShouldBeANumberButIsnt(value);
        }
        return underlyingRule(value);
    };
}

export const positive = decoratorAllowEmptyInput(
    decoratorExpectNumeric((value: number): true | string => {
        if (value > 0) {
            return true;
        }
        return 'This field must be a positive number';
    }),
);
export const nonNegative = decoratorAllowEmptyInput(
    decoratorExpectNumeric((value: number): true | string => {
        if (value >= 0) {
            return true;
        }
        return 'This field must be a non-negative number';
    }),
);
export const integer = decoratorAllowEmptyInput(
    decoratorExpectNumeric((value: number): true | string => {
        if (Number.isInteger(value)) {
            return true;
        }
        return 'This field must be an integer';
    }),
);
export function ruleGetterMaxValue(maxValue: number): TypeValidationRuleNumber {
    return decoratorAllowEmptyInput(
        decoratorExpectNumeric((value: number): true | string => {
            if (value <= maxValue) {
                return true;
            }
            return `This field must be less than or equal to ${maxValue}`;
        }),
    );
}

export const noSpecialCharacters = decoratorAllowEmptyInput((value: string): true | string => {
    return !/[!@#$%^&*,?":{}|<>]/.test(value) || 'This field must not contain special characters.';
});

export function checkboxAtLeastOneSelected(value): true | string {
    const checkedCount = Object.values(value).filter((v) => v).length;
    return checkedCount > 0 || 'Please select at least one option';
}

export const phone = decoratorAllowEmptyInput((value: string): true | string => {
    // Retrieved from https://stackoverflow.com/a/16702965/214762
    const phoneNumberRegex =
        /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;
    return phoneNumberRegex.test(value) || 'This field must be a valid phone number';
});

// TODO: Remove at PR These rules and their usages should be converted to vuetify rules, then removed:
function extendValidationRules() {
    defineRule('email', (_value) => {
        return 'Not a valid email address';
    });

    defineRule('password', (value) => {
        // retrieved from https://stackoverflow.com/a/21456918
        // Followed policies from https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html
        const passwordRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,99}$/;
        return (
            passwordRegex.test(value) ||
            `This field must be between 6 and 99 characters long and include:
        - at least one number
        - at least one special character from @$!%*?&
        - at least one uppercase letter
        - at least one lowercase letter`
        );
    });
}

function doValidationStandardSetup() {
    extendValidationRules();
}

/**
 * A Vue plugin that registers the validation rules with the Vue instance
 */
export default {
    install(_vue, _options) {
        // Register common (app-wide) components
        doValidationStandardSetup();
    },
};
